<template>
  <div class="stats-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-wrapper">
        <a-spin size="large" />
        <div class="loading-text">
          {{ $t("common.loading") || "Chargement des statistiques..." }}
        </div>
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>

    <!-- Stats Content -->
    <div v-else-if="!error" class="stats-content">
      <!-- Header Section -->
      <div class="stats-header">
        <h2 class="main-title">
          {{ $t("recette.stats.financialDashboard") }}
        </h2>
      </div>

      <!-- Rejected Cheques Alert -->
      <div
        v-if="rejectedChequesInfo.hasRejected"
        class="rejected-alert-container"
      >
        <a-alert
          :message="
            $t('recette.stats.rejectedChequesAlert') ||
            'Chèques rejetés détectés'
          "
          type="warning"
          show-icon
          closable
        >
          <template slot="description">
            <div class="rejected-details">
              <p class="rejected-summary">
                <strong>{{ rejectedChequesInfo.totalCount }}</strong>
                {{
                  rejectedChequesInfo.totalCount > 1
                    ? $t("recette.stats.rejectedPayments")
                    : $t("recette.stats.rejectedPayment")
                }}
                {{ $t("recette.stats.forTotalAmount") }}
                <strong>{{
                  formatAmount(rejectedChequesInfo.totalAmount)
                }}</strong>
              </p>
              <div class="rejected-breakdown">
                <span
                  v-for="detail in rejectedChequesInfo.details"
                  :key="detail.type"
                  class="rejected-item"
                >
                  <a-tag color="red" class="rejected-tag">
                    {{ detail.label }}: {{ detail.count }} ({{
                      formatAmount(detail.amount)
                    }})
                  </a-tag>
                </span>
              </div>
            </div>
          </template>
        </a-alert>
      </div>

      <!-- Quick Summary Cards at Top -->
      <div class="quick-summary">
        <div class="summary-grid">
          <div
            v-for="(summary, index) in summaryStats"
            :key="summary.type"
            class="summary-card-enhanced"
            :style="{ '--animation-delay': index * 0.1 + 's' }"
          >
            <div class="card-icon" :style="{ backgroundColor: summary.color }">
              <a-icon :type="getTypeIcon(summary.type)" />
            </div>
            <div class="card-content">
              <div class="card-title">{{ getTypeLabel(summary.type) }}</div>
              <div class="card-amount">
                {{ formatAmount(summary.totalAmount) }}
              </div>
              <div class="card-count">
                {{ summary.totalCount }} {{ $t("recette.stats.transactions") }}
              </div>
            </div>
            <div
              class="card-decoration"
              :style="{
                background: `linear-gradient(135deg, ${summary.color}20, ${summary.color}05)`,
              }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Bank Status Statistics with Enhanced Design -->
      <div class="section-container">
        <div class="section-header">
          <h3 class="section-title">
            <a-icon type="bank" class="section-icon" />
            {{ $t("recette.stats.bankStatus") }}
          </h3>
          <div class="section-decoration"></div>
        </div>

        <div class="cards-grid">
          <div
            v-for="(typeStats, index) in statsByBankStatus"
            :key="typeStats._id"
            class="enhanced-card"
            :style="{ '--animation-delay': index * 0.15 + 's' }"
          >
            <div
              class="card-header"
              :style="{
                background: `linear-gradient(135deg, ${getTypeColor(
                  typeStats._id
                )}, ${getTypeColor(typeStats._id)}dd)`,
              }"
            >
              <div class="card-type-icon">
                <a-icon :type="getTypeIcon(typeStats._id)" />
              </div>
              <div class="card-type-info">
                <h4>{{ getTypeLabel(typeStats._id) }}</h4>
                <p>
                  {{ typeStats.totalCount }}
                  {{ $t("recette.stats.elements") }} •
                  {{ formatAmount(typeStats.totalAmount) }}
                </p>
              </div>
            </div>

            <div class="card-body">
              <div class="status-items">
                <div
                  v-for="bankStatus in typeStats.bankStatuses"
                  :key="bankStatus.bankStatus"
                  class="status-item-enhanced"
                >
                  <div class="status-indicator">
                    <div
                      class="status-dot"
                      :style="{
                        backgroundColor: getBankStatusColor(
                          bankStatus.bankStatus
                        ),
                      }"
                    ></div>
                    <span class="status-label">{{
                      getBankStatusLabel(bankStatus.bankStatus)
                    }}</span>
                  </div>
                  <div class="status-metrics">
                    <div class="status-count">{{ bankStatus.count }}</div>
                    <div class="status-amount">
                      {{ formatAmount(bankStatus.totalAmount) }}
                    </div>
                  </div>
                  <div class="status-progress">
                    <div
                      class="progress-bar"
                      :style="{
                        width:
                          (bankStatus.count / typeStats.totalCount) * 100 + '%',
                        backgroundColor: getBankStatusColor(
                          bankStatus.bankStatus
                        ),
                      }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- General Status Statistics -->
      <div class="section-container">
        <div class="section-header">
          <h3 class="section-title">
            <a-icon type="file-done" class="section-icon" />
            {{ $t("recette.stats.generalStatus") }}
          </h3>
          <div class="section-decoration"></div>
        </div>

        <div class="cards-grid">
          <div
            v-for="(typeStats, index) in statsByStatus"
            :key="typeStats._id"
            class="enhanced-card"
            :style="{ '--animation-delay': index * 0.15 + 's' }"
          >
            <div
              class="card-header"
              :style="{
                background: `linear-gradient(135deg, ${getTypeColor(
                  typeStats._id
                )}, ${getTypeColor(typeStats._id)}dd)`,
              }"
            >
              <div class="card-type-icon">
                <a-icon :type="getTypeIcon(typeStats._id)" />
              </div>
              <div class="card-type-info">
                <h4>{{ getTypeLabel(typeStats._id) }}</h4>
                <p>
                  {{ typeStats.totalCount }}
                  {{ $t("recette.stats.elements") }} •
                  {{ formatAmount(typeStats.totalAmount) }}
                </p>
              </div>
            </div>

            <div class="card-body">
              <div class="status-items">
                <div
                  v-for="status in typeStats.bankStatuses"
                  :key="status.status"
                  class="status-item-enhanced"
                >
                  <div class="status-indicator">
                    <div
                      class="status-dot"
                      :style="{
                        backgroundColor: getStatusColor(status.status),
                      }"
                    ></div>
                    <span class="status-label">{{
                      getStatusLabel(status.status)
                    }}</span>
                  </div>
                  <div class="status-metrics">
                    <div class="status-count">{{ status.count }}</div>
                    <div class="status-amount">
                      {{ formatAmount(status.totalAmount) }}
                    </div>
                  </div>
                  <div class="status-progress">
                    <div
                      class="progress-bar"
                      :style="{
                        width:
                          (status.count / typeStats.totalCount) * 100 + '%',
                        backgroundColor: getStatusColor(status.status),
                      }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Error State -->
    <div v-if="error" class="error-container">
      <div class="error-content">
        <div class="error-icon">
          <a-icon type="exclamation-circle" />
        </div>
        <h3>{{ $t("error.oops") || "Oops! Une erreur s'est produite" }}</h3>
        <p>{{ error }}</p>
        <a-button type="primary" size="large" @click="fetchStats">
          <a-icon type="reload" />
          {{ $t("action.retry") || "Réessayer" }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import { formatCurrency } from "@/utilities/countrySpecific";

export default {
  name: "StatsTab",

  computed: {
    ...mapState(["settings"]),

    summaryStats() {
      const summary = {};

      // Aggregate from statsByBankStatus
      this.statsByBankStatus.forEach((typeStats) => {
        summary[typeStats._id] = {
          type: typeStats._id,
          totalCount: typeStats.totalCount,
          totalAmount: typeStats.totalAmount,
          color: this.getTypeColor(typeStats._id),
        };
      });

      return Object.values(summary);
    },

    rejectedChequesInfo() {
      let rejectedDetails = [];
      let totalRejected = 0;
      let totalAmount = 0;

      this.statsByBankStatus.forEach((typeStats) => {
        const rejectedStatus = typeStats.bankStatuses.find(
          (status) => status.bankStatus === "REJECTED"
        );
        if (rejectedStatus && rejectedStatus.rejectedNotReplaced > 0) {
          rejectedDetails.push({
            type: typeStats._id,
            count: rejectedStatus.rejectedNotReplaced,
            amount: rejectedStatus.rejectedNotReplacedAmount,
            label: this.getTypeLabel(typeStats._id),
          });
          totalRejected += rejectedStatus.rejectedNotReplaced;
          totalAmount += rejectedStatus.rejectedNotReplacedAmount;
        }
      });

      return {
        hasRejected: rejectedDetails.length > 0,
        details: rejectedDetails,
        totalCount: totalRejected,
        totalAmount: totalAmount,
      };
    },
  },

  data() {
    return {
      loading: false,
      error: null,
      statsByBankStatus: [],
      statsByStatus: [],
    };
  },

  async mounted() {
    await this.fetchStats();
  },

  methods: {
    async fetchStats() {
      this.loading = true;
      this.error = null;

      try {
        const response = await apiClient.get("/compV3/cheque-traite/stats");

        this.statsByBankStatus = response.data.statsByBankStatus || [];
        this.statsByStatus = response.data.statsByStatus || [];
      } catch (error) {
        console.error("Error fetching stats:", error);
        this.error =
          error.response?.data?.message ||
          this.$t("recette.stats.loadingStatsError");
      } finally {
        this.loading = false;
      }
    },

    formatAmount(amount) {
      return formatCurrency(amount);
    },

    getBankStatusColor(bankStatus) {
      const colors = {
        VALIDATED: "#52c41a",
        PENDING: "#faad14",
        REJECTED: "#ff4d4f",
      };
      return colors[bankStatus] || "#d9d9d9";
    },

    getBankStatusLabel(bankStatus) {
      return this.$t(`status.${bankStatus}`) || bankStatus;
    },

    getStatusColor(status) {
      const colors = {
        ACTIVE: "#1890ff",
        CANCELLED: "#ff4d4f",
        REPLACED: "#faad14",
      };
      return colors[status] || "#d9d9d9";
    },

    getStatusLabel(status) {
      return this.$t(`status.${status}`);
    },

    getTypeColor(type) {
      const colors = {
        CHEQUE: "#1890ff",
        TRAITE: "#52c41a",
        VIREMENT: "#fa8c16",
      };
      return colors[type] || "#666";
    },

    getTypeIcon(type) {
      const icons = {
        CHEQUE: "credit-card",
        TRAITE: "bank",
        VIREMENT: "swap",
      };
      return icons[type] || "file-text";
    },

    getTypeLabel(type) {
      return this.$t(`recette.paymentMethod.${type}`) || type;
    },
  },
};
</script>

<style scoped>
.stats-container {
  min-height: 100vh;
  background: #ffffff;
  padding: 0;
}

/* Enhanced Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.loading-wrapper {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.loading-text {
  margin-top: 20px;
  font-size: 18px;
  color: #666;
  font-weight: 500;
}

.loading-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1890ff;
  animation: bounce 1.4s ease-in-out infinite both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}
.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

/* Stats Content */
.stats-content {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.stats-header {
  margin-bottom: 40px;
  text-align: center;
}

.main-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  color: #1a1a1a;
}

/* Quick Summary */
.quick-summary {
  margin-bottom: 40px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.summary-card-enhanced {
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 5px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: slideInUp 0.6s ease-out both;
  animation-delay: var(--animation-delay);
}

.summary-card-enhanced:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0;
}

.card-content {
  flex: 1;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
}

.card-amount {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.card-count {
  font-size: 12px;
  color: #999;
}

.card-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100%;
  border-radius: 0 20px 20px 0;
}

/* Section Container */
.section-container {
  margin-bottom: 40px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  position: relative;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-icon {
  font-size: 28px;
  color: #1890ff;
}

.section-decoration {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #1890ff, transparent);
  margin-left: 20px;
}

/* Enhanced Cards Grid */
.cards-grid {
  display: flex;
  gap: 24px;
  flex-wrap: nowrap;
  overflow-x: auto;
}

.enhanced-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  animation: slideInUp 0.6s ease-out both;
  animation-delay: var(--animation-delay);
  flex: 1;
  min-width: 320px;
}

.enhanced-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.card-header {
  padding: 24px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
}

.card-type-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.card-type-info h4 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

.card-type-info p {
  margin: 4px 0 0 0;
  opacity: 0.9;
  font-size: 14px;
}

.card-body {
  padding: 24px;
}

/* Status Items */
.status-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-item-enhanced {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.status-item-enhanced:hover {
  background: #f0f2f5;
  transform: translateX(4px);
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-label {
  font-weight: 600;
  color: #1a1a1a;
}

.status-metrics {
  text-align: right;
  min-width: 120px;
}

.status-count {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.status-amount {
  font-size: 12px;
  color: #666;
}

.status-progress {
  width: 60px;
  height: 6px;
  background: #e8e8e8;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* Error State */
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 40px;
}

.error-content {
  text-align: center;
  padding: 60px 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  max-width: 500px;
}

.error-icon {
  font-size: 64px;
  color: #ff4d4f;
  margin-bottom: 24px;
}

.error-content h3 {
  font-size: 24px;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.error-content p {
  color: #666;
  margin-bottom: 32px;
  font-size: 16px;
}

.error-content .ant-btn {
  border-radius: 12px;
  height: 48px;
  padding: 0 32px;
  font-weight: 500;
}

/* Rejected Cheques Alert Styles */
.rejected-alert-container {
  margin-bottom: 30px;
}

.rejected-details {
  margin-top: 8px;
}

.rejected-summary {
  font-size: 15px;
  margin-bottom: 12px;
  color: #d46b08;
}

.rejected-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rejected-tag {
  font-size: 13px;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .main-title {
    font-size: 24px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .cards-grid {
    flex-direction: column;
    overflow-x: visible;
  }

  .enhanced-card {
    min-width: unset;
  }

  .section-title {
    font-size: 20px;
  }
}

@media (max-width: 480px) {
  .summary-card-enhanced {
    padding: 16px;
  }

  .card-amount {
    font-size: 20px;
  }

  .status-item-enhanced {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .status-metrics {
    align-self: flex-end;
  }
}
</style>
